<template>
  <div id="app">
    <app-bar></app-bar>
    <router-view />
    <fullscreen-menu/>
    <dialogs-wrapper transition-name="fade" />
    <transition name="fade">
      <loading-screen/>
    </transition>
    <!-- Offline tracker -->
    <offline-tracker />
  </div>
</template>

<script>
/* global FastClick */
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import { DialogsWrapper } from 'vue-modal-dialogs';
import AppBar from '@/components/AppBar';
import FullscreenMenu from '@/components/FullscreenMenu';
import OfflineTracker from '@/components/OfflineTracker';
import LoadingScreen from '@/components/LoadingScreen';

export default {
  name: 'App',
  computed: {
    ...mapState({
      themeColours: state => state.project.theme.colours,
      menuActive: state => state.ui.menuActive,
      appReady: state => state.appReady,
      pageReady: state => state.ui.pageReady,
      forPublic: state => state.project.forPublic
    }),
    ...mapGetters({
      deviceMd: 'ui/deviceMd',
      isLoggedIn: 'user/isLoggedIn'
    }),
    showMenu() {
      return this.menuActive;
    },
  },
  methods: {
    ...mapActions({
      fetchSections: 'project/fetchSections',
      fetchSettings: 'project/fetchSettings',
      initSocket: 'project/initSocket'
    }),
    ...mapMutations({
      setWindowWidth: 'ui/setWindowWidth',
      setPageReady: 'ui/setPageReady',
      setIdle: 'ui/setIdle'
    }),
    handleResize() {
      this.setWindowWidth(window.innerWidth);
    },
    setThemeColours() {
      // Set theme colours
      const rootStyle = document.documentElement.style;
      const colours = this.themeColours;
      colours.forEach((colour) => {
        rootStyle.setProperty(`--${colour.name}-h`, colour.h);
        rootStyle.setProperty(`--${colour.name}-s`, colour.s);
        rootStyle.setProperty(`--${colour.name}-l`, colour.l);
        rootStyle.setProperty(
          `--${colour.name}`,
          `hsl(${colour.h}, ${colour.s}, ${colour.l})`
        );
      });
    }
  },
  created() {
    this.fetchSettings()
      .then(this.fetchSections);
    this.initSocket();

    // Enable fast-click
    if ('addEventListener' in document) {
      document.addEventListener('DOMContentLoaded', () => {
        FastClick.attach(document.body);
      }, false);
    }

    // Screensize detector
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
  },
  mounted() {
    this.setThemeColours();
  },
  watch: {
    themeColours() {
      this.setThemeColours();
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  components: {
    'app-bar': AppBar,
    'fullscreen-menu': FullscreenMenu,
    'offline-tracker': OfflineTracker,
    'loading-screen': LoadingScreen,
    'dialogs-wrapper': DialogsWrapper
  }
};
</script>

<style lang="scss">

@font-face {
  font-family: "Scandia-Regular";
  src: url("../static/fonts/scandia/Scandia-Regular.otf") format("opentype");
}

@font-face {
  font-family: "Scandia-Bold";
  src: url("../static/fonts/scandia/Scandia-Bold.otf") format("opentype");
}

@font-face {
  font-family: "Scandia-Light";
  src: url("../static/fonts/scandia/Scandia-Light.otf") format("opentype");
}

@font-face {
  font-family: "Scandia-Medium";
  src: url("../static/fonts/scandia/Scandia-Medium.otf") format("opentype");
}

@font-face {
  font-family: "Didot-Regular";
  src: url("../static/fonts/didot/Didot-Regular.ttf") format("truetype");
}

@font-face {
  font-family: "Didot-Bold";
  src: url("../static/fonts/didot/Didot-Bold.ttf") format("truetype");
}


#app {
  font-family: Scandia-Regular, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: var(--text);
  user-select: none;
}
</style>



// WEBPACK FOOTER //
// src/App.vue